import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2,
  Plug,
  Trash2,
  ExternalLink,
  CheckCircle2,
  FileText,
  Wand2,
  Globe,
} from "lucide-react";

function ConnectForm({ onConnected }) {
  const [form, setForm] = useState({ name: "My WordPress Site", site_url: "", username: "", app_password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/wordpress/connections", form);
      toast.success("WordPress connected");
      onConnected(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl">
      <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-6">
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Connection name</Label>
            <Input data-testid="wp-name-input" className="mt-1.5" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>WordPress site URL</Label>
            <Input data-testid="wp-url-input" className="mt-1.5" placeholder="https://yourblog.com" value={form.site_url}
              onChange={(e) => setForm({ ...form, site_url: e.target.value })} required />
          </div>
          <div>
            <Label>WordPress username</Label>
            <Input data-testid="wp-username-input" className="mt-1.5" value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })} required />
          </div>
          <div>
            <Label>Application Password</Label>
            <Input data-testid="wp-password-input" type="password" className="mt-1.5" placeholder="xxxx xxxx xxxx xxxx"
              value={form.app_password} onChange={(e) => setForm({ ...form, app_password: e.target.value })} required />
          </div>
          {error && <p data-testid="wp-connect-error" className="text-sm text-bad font-medium">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="wp-connect-button" className="w-full bg-[#0A0A0A] hover:bg-gray-800 py-6">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plug className="w-4 h-4 mr-2" /> Connect WordPress</>}
          </Button>
        </form>
      </div>
      <div className="mt-4 bg-gray-50 border-l-4 border-l-klein rounded-r-lg p-4 text-sm text-gray-600">
        <p className="font-semibold text-gray-800 mb-1">How to get an Application Password</p>
        In WordPress: <b>Users → Profile → Application Passwords</b>, enter a name like "Sapientia", click
        <b> Add New Application Password</b>, and copy the generated value. Your site must use HTTPS.
      </div>
    </div>
  );
}

function ApplyDialog({ conn, item, onClose }) {
  const [title, setTitle] = useState(item?.title || "");
  const [meta, setMeta] = useState("");
  const [loading, setLoading] = useState(false);

  const push = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/wordpress/connections/${conn.id}/apply`, {
        object_type: item.type,
        object_id: item.id,
        title: title || undefined,
        meta_description: meta || undefined,
      });
      toast.success("Pushed to WordPress", { description: data.link || "Update applied" });
      onClose();
    } catch (e) {
      toast.error("Could not apply", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="wp-apply-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Optimize "{item?.title}"</DialogTitle>
          <DialogDescription>Push a new SEO title and/or meta description to this {item?.type}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>SEO Title</Label>
            <Input data-testid="wp-apply-title" className="mt-1.5" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <Label>Meta Description</Label>
            <Textarea data-testid="wp-apply-meta" className="mt-1.5" rows={3} value={meta} onChange={(e) => setMeta(e.target.value)} placeholder="150–160 characters" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-gray-300">Cancel</Button>
          <Button onClick={push} disabled={loading} data-testid="wp-apply-submit" className="bg-[#0A0A0A] hover:bg-gray-800">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4 mr-2" /> Push to WordPress</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function WordPress() {
  const [conns, setConns] = useState(null);
  const [content, setContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [active, setActive] = useState(null);

  const load = () => api.get("/wordpress/connections").then((r) => setConns(r.data)).catch(() => setConns([]));
  useEffect(() => { load(); }, []);

  const conn = conns && conns[0];

  useEffect(() => {
    if (conn) {
      setLoadingContent(true);
      api.get(`/wordpress/connections/${conn.id}/content`)
        .then((r) => setContent(r.data))
        .catch(() => setContent([]))
        .finally(() => setLoadingContent(false));
    }
  }, [conn?.id]);

  const disconnect = async () => {
    await api.delete(`/wordpress/connections/${conn.id}`);
    setContent(null);
    load();
    toast.success("Disconnected");
  };

  if (conns === null)
    return <div className="flex justify-center pt-20"><Loader2 className="w-8 h-8 animate-spin text-klein" /></div>;

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">WordPress</h1>
        <p className="text-gray-500 mt-2">Connect your site and push SEO fixes to live pages in one click.</p>
      </div>

      {!conn ? (
        <ConnectForm onConnected={() => load()} />
      ) : (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="wp-connection-card">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-good/10 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-good" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-gray-900 flex items-center gap-2">
                  {conn.name}
                  {conn.has_yoast && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">Yoast</span>}
                  {conn.has_rank_math && <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">Rank Math</span>}
                </div>
                <a href={conn.site_url} target="_blank" rel="noreferrer" className="text-sm text-gray-500 hover:underline flex items-center gap-1">
                  <Globe className="w-3.5 h-3.5" /> {conn.site_url}
                </a>
              </div>
            </div>
            <Button variant="outline" onClick={disconnect} data-testid="wp-disconnect-button" className="text-bad border-gray-300 hover:bg-red-50">
              <Trash2 className="w-4 h-4 mr-2" /> Disconnect
            </Button>
          </div>

          <div className="bg-white border border-gray-200 shadow-sm rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="uppercase-label">Posts & Pages</h3>
            </div>
            {loadingContent ? (
              <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-klein" /></div>
            ) : !content || content.length === 0 ? (
              <div className="p-10 text-center text-gray-500 text-sm" data-testid="wp-content-empty">No posts or pages found.</div>
            ) : (
              <div className="divide-y divide-gray-100" data-testid="wp-content-list">
                {content.map((it) => (
                  <div key={`${it.type}-${it.id}`} className="flex items-center gap-3 px-5 py-3.5" data-testid={`wp-item-${it.type}-${it.id}`}>
                    <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900 truncate">{it.title}</div>
                      <div className="text-xs text-gray-400 capitalize">{it.type} · {it.status}</div>
                    </div>
                    {it.link && <a href={it.link} target="_blank" rel="noreferrer" className="text-gray-300 hover:text-gray-600"><ExternalLink className="w-4 h-4" /></a>}
                    <Button size="sm" onClick={() => setActive(it)} data-testid={`wp-optimize-${it.type}-${it.id}`} className="bg-[#0A0A0A] hover:bg-gray-800">
                      <Wand2 className="w-3.5 h-3.5 mr-1.5" /> Optimize
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {active && conn && <ApplyDialog conn={conn} item={active} onClose={() => setActive(null)} />}
    </div>
  );
}
